export interface CalculatorProgressMessage {
  type: string;
  data?: { [key: string]: unknown };
}

/** Required steps for points: 5 input pages + Summary. Take Action is optional. */
export const CALCULATOR_PAGE_COUNT = 6;

export type CalculatorPageId =
  | 'get_started'
  | 'travel'
  | 'home'
  | 'food'
  | 'shopping'
  | 'take_action';

export const CALCULATOR_PAGES: CalculatorPageId[] = [
  'get_started',
  'travel',
  'home',
  'food',
  'shopping'
];

const PAGE_INPUT_PREFIXES: { [page: string]: string[] } = {
  get_started: ['input_location', 'input_size', 'input_income'],
  travel: ['input_footprint_transportation_'],
  home: ['input_footprint_housing_'],
  food: ['input_footprint_shopping_food_'],
  shopping: ['input_footprint_shopping_goods_', 'input_footprint_shopping_services_']
};

/** Fresh-calculator bootstrap demographics (Firebase average_footprint seed). */
export const GET_STARTED_BOOTSTRAP_DEFAULTS: { [key: string]: string } = {
  input_location_mode: '5',
  input_location: '',
  input_location_country: 'US',
  input_income: '1',
  input_size: '0'
};

const CORE_INPUT_KEYS = [
  'input_location_mode',
  'input_location',
  'input_location_country',
  'input_income',
  'input_size'
];

export interface CalculatorProgressState {
  visited: CalculatorPageId[];
  changed: { [page: string]: boolean };
  /** User opened the climate summary (Next.js `/results/summary`) at least once. */
  summaryVisited: boolean;
  /** True while the iframe is currently on the Summary route. */
  onSummaryPage: boolean;
  /** Location-based defaults from Calculator API (and *_default twins). */
  defaultInputs: { [key: string]: string };
  latestInputs: { [key: string]: unknown };
  latestActionsTaken: string[];
}

export const createCalculatorProgressState = (): CalculatorProgressState => ({
  visited: [],
  changed: {},
  summaryVisited: false,
  onSummaryPage: false,
  defaultInputs: { ...GET_STARTED_BOOTSTRAP_DEFAULTS },
  latestInputs: {},
  latestActionsTaken: []
});

const isCalculatorPageId = (page: unknown): page is CalculatorPageId => {
  if (typeof page !== 'string') {
    return false;
  }
  // Accept take_action visits for messaging, even though it is not required.
  if (page === 'take_action') {
    return true;
  }
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    if (CALCULATOR_PAGES[i] === page) {
      return true;
    }
  }
  return false;
};

export const normalizeVisitedPages = (visited: unknown): CalculatorPageId[] => {
  if (!Array.isArray(visited)) {
    return [];
  }
  const pages: CalculatorPageId[] = [];
  for (let i = 0; i < visited.length; i++) {
    if (isCalculatorPageId(visited[i])) {
      pages.push(visited[i]);
    }
  }
  return pages;
};

const keysForPage = (page: CalculatorPageId, inputs: { [key: string]: unknown }): string[] => {
  const prefixes = PAGE_INPUT_PREFIXES[page] || [];
  const keys: string[] = [];
  const inputKeys = Object.keys(inputs);
  for (let i = 0; i < inputKeys.length; i++) {
    const key = inputKeys[i];
    if (key.indexOf('_default') >= 0) {
      continue;
    }
    for (let j = 0; j < prefixes.length; j++) {
      const prefix = prefixes[j];
      if (key === prefix || key.indexOf(prefix) === 0) {
        keys.push(key);
        break;
      }
    }
  }
  return keys;
};

export const serializeInputValue = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value);

const pageDiffersFromDefaults = (
  page: CalculatorPageId,
  inputs: { [key: string]: unknown },
  defaults: { [key: string]: string }
): boolean => {
  // Get Started is compared to the calculator bootstrap seed, not to
  // get_defaults_and_results (which echoes the user's current demographics).
  if (page === 'get_started') {
    const bootstrapKeys = Object.keys(GET_STARTED_BOOTSTRAP_DEFAULTS);
    for (let i = 0; i < bootstrapKeys.length; i++) {
      const key = bootstrapKeys[i];
      if (!Object.prototype.hasOwnProperty.call(inputs, key)) {
        continue;
      }
      if (serializeInputValue(inputs[key]) !== GET_STARTED_BOOTSTRAP_DEFAULTS[key]) {
        return true;
      }
    }
    return false;
  }

  const keys = keysForPage(page, inputs);
  if (!keys.length) {
    return false;
  }
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!Object.prototype.hasOwnProperty.call(defaults, key)) {
      continue;
    }
    if (serializeInputValue(inputs[key]) !== defaults[key]) {
      return true;
    }
  }
  return false;
};

/** Pull `foo_default` twins (and shopping `goods_default_bar`) into a defaults map keyed as `foo`. */
export const extractDefaultTwins = (
  inputs: { [key: string]: unknown }
): { [key: string]: string } => {
  const defaults: { [key: string]: string } = {};
  const keys = Object.keys(inputs || {});
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (key.length > 8 && key.indexOf('_default') === key.length - 8) {
      const base = key.slice(0, key.length - 8);
      defaults[base] = serializeInputValue(inputs[key]);
      continue;
    }
    const goodsPrefix = 'input_footprint_shopping_goods_default_';
    if (key.indexOf(goodsPrefix) === 0) {
      const suffix = key.slice(goodsPrefix.length);
      defaults[`input_footprint_shopping_goods_${suffix}`] = serializeInputValue(inputs[key]);
    }
  }
  return defaults;
};

export const coreDefaultsCacheKey = (inputs: { [key: string]: unknown }): string =>
  CORE_INPUT_KEYS.map((key) => serializeInputValue(inputs[key])).join('|');

const mergeDefaults = (
  existing: { [key: string]: string },
  incoming: { [key: string]: string }
): { [key: string]: string } => {
  const merged: { [key: string]: string } = {};
  const existingKeys = Object.keys(existing);
  for (let i = 0; i < existingKeys.length; i++) {
    merged[existingKeys[i]] = existing[existingKeys[i]];
  }
  const incomingKeys = Object.keys(incoming);
  for (let i = 0; i < incomingKeys.length; i++) {
    merged[incomingKeys[i]] = incoming[incomingKeys[i]];
  }
  return merged;
};

const ensureGetStartedVisited = (state: CalculatorProgressState): CalculatorProgressState => {
  if (state.visited.length || !Object.keys(state.latestInputs).length) {
    return state;
  }
  return {
    visited: ['get_started'],
    changed: state.changed,
    summaryVisited: state.summaryVisited,
    onSummaryPage: state.onSummaryPage,
    defaultInputs: state.defaultInputs,
    latestInputs: state.latestInputs,
    latestActionsTaken: state.latestActionsTaken
  };
};

const recomputeChangedPages = (state: CalculatorProgressState): CalculatorProgressState => {
  // Accumulate — never drop a page once customized (avoids races that flash 2→0).
  const changed: { [page: string]: boolean } = {};
  const existingKeys = Object.keys(state.changed);
  for (let i = 0; i < existingKeys.length; i++) {
    if (state.changed[existingKeys[i]]) {
      changed[existingKeys[i]] = true;
    }
  }
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    const page = CALCULATOR_PAGES[i];
    if (state.visited.indexOf(page) < 0) {
      continue;
    }
    if (pageDiffersFromDefaults(page, state.latestInputs, state.defaultInputs)) {
      changed[page] = true;
    }
  }
  return {
    visited: state.visited,
    changed,
    summaryVisited: state.summaryVisited,
    onSummaryPage: state.onSummaryPage,
    defaultInputs: state.defaultInputs,
    latestInputs: state.latestInputs,
    latestActionsTaken: state.latestActionsTaken
  };
};

/** Prefer explicit `changed` flags from the Next calculator iframe when present. */
const mergeExplicitChanged = (
  state: CalculatorProgressState,
  data: { [key: string]: unknown }
): CalculatorProgressState => {
  const incoming = data.changed;
  if (!incoming || typeof incoming !== 'object') {
    return state;
  }
  const changed: { [page: string]: boolean } = {};
  const existingKeys = Object.keys(state.changed);
  for (let i = 0; i < existingKeys.length; i++) {
    changed[existingKeys[i]] = state.changed[existingKeys[i]];
  }
  const keys = Object.keys(incoming as { [key: string]: unknown });
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if ((incoming as { [key: string]: unknown })[key] === true) {
      changed[key] = true;
    }
  }
  return {
    visited: state.visited,
    changed,
    summaryVisited: state.summaryVisited,
    onSummaryPage: state.onSummaryPage,
    defaultInputs: state.defaultInputs,
    latestInputs: state.latestInputs,
    latestActionsTaken: state.latestActionsTaken
  };
};

/**
 * Merge Calculator API / twin defaults, then recompute which visited pages differ
 * from defaults (not from previously saved user answers).
 */
export const applyCalculatorDefaults = (
  state: CalculatorProgressState,
  defaults: { [key: string]: unknown }
): CalculatorProgressState => {
  const serialized: { [key: string]: string } = {};
  const keys = Object.keys(defaults || {});
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (key.length > 8 && key.indexOf('_default') === key.length - 8) {
      continue;
    }
    if (key.indexOf('input_') !== 0) {
      continue;
    }
    serialized[key] = serializeInputValue(defaults[key]);
  }
  const twins = extractDefaultTwins(defaults);
  const nextDefaults = mergeDefaults(mergeDefaults(state.defaultInputs, serialized), twins);
  return recomputeChangedPages({
    visited: state.visited,
    changed: state.changed,
    summaryVisited: state.summaryVisited,
    onSummaryPage: state.onSummaryPage,
    defaultInputs: nextDefaults,
    latestInputs: state.latestInputs,
    latestActionsTaken: state.latestActionsTaken
  });
};

const applyInputsUpdate = (
  state: CalculatorProgressState,
  data: { [key: string]: unknown }
): CalculatorProgressState => {
  const inputs = (data.inputs as { [key: string]: unknown }) || {};
  const actionsTaken: string[] = [];
  if (Array.isArray(data.actionsTaken)) {
    for (let i = 0; i < data.actionsTaken.length; i++) {
      const action = data.actionsTaken[i];
      if (typeof action === 'string') {
        actionsTaken.push(action);
      }
    }
  } else {
    for (let i = 0; i < state.latestActionsTaken.length; i++) {
      actionsTaken.push(state.latestActionsTaken[i]);
    }
  }

  const latestInputs: { [key: string]: unknown } = {};
  const existingKeys = Object.keys(state.latestInputs);
  for (let i = 0; i < existingKeys.length; i++) {
    latestInputs[existingKeys[i]] = state.latestInputs[existingKeys[i]];
  }
  const inputKeys = Object.keys(inputs);
  for (let i = 0; i < inputKeys.length; i++) {
    latestInputs[inputKeys[i]] = inputs[inputKeys[i]];
  }

  const twins = extractDefaultTwins(inputs);
  const defaultInputs = Object.keys(twins).length
    ? mergeDefaults(state.defaultInputs, twins)
    : state.defaultInputs;

  const next = ensureGetStartedVisited({
    visited: state.visited,
    changed: state.changed,
    summaryVisited: state.summaryVisited,
    onSummaryPage: state.onSummaryPage,
    defaultInputs,
    latestInputs,
    latestActionsTaken: actionsTaken
  });
  return mergeExplicitChanged(recomputeChangedPages(next), data);
};

const applyPageVisited = (
  state: CalculatorProgressState,
  data: { [key: string]: unknown }
): CalculatorProgressState => {
  const incomingVisited = normalizeVisitedPages(data.visited);
  // Union visits — never shrink the list if a stale/partial message arrives.
  const visitedSet: { [page: string]: boolean } = {};
  for (let i = 0; i < state.visited.length; i++) {
    visitedSet[state.visited[i]] = true;
  }
  for (let i = 0; i < incomingVisited.length; i++) {
    visitedSet[incomingVisited[i]] = true;
  }
  const visited: CalculatorPageId[] = [];
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    if (visitedSet[CALCULATOR_PAGES[i]]) {
      visited.push(CALCULATOR_PAGES[i]);
    }
  }
  if (visitedSet.take_action) {
    visited.push('take_action');
  }
  if (!visited.length && data.summaryVisited !== true) {
    return mergeExplicitChanged(
      {
        ...state,
        onSummaryPage: false
      },
      data
    );
  }
  const summaryVisited =
    data.summaryVisited === true ? true : state.summaryVisited;
  const onSummaryPage = data.summaryVisited === true;
  return mergeExplicitChanged(
    recomputeChangedPages({
      visited: visited.length ? visited : state.visited,
      changed: state.changed,
      summaryVisited,
      onSummaryPage,
      defaultInputs: state.defaultInputs,
      latestInputs: state.latestInputs,
      latestActionsTaken: state.latestActionsTaken
    }),
    data
  );
};

export const applyCalculatorProgressUpdate = (
  state: CalculatorProgressState,
  message: CalculatorProgressMessage
): CalculatorProgressState => {
  if (!message || !message.type) {
    return state;
  }

  if (message.type === 'inputs:update' && message.data) {
    return applyInputsUpdate(state, message.data);
  }

  if (message.type === 'page:visited' && message.data) {
    // Do not re-run inputs:update without the message's `changed` flags — that
    // used to wipe progress when twin detection briefly disagreed with the iframe.
    return applyPageVisited(state, message.data);
  }

  return state;
};

export const visitedStepsCount = (state: CalculatorProgressState): number => {
  let count = 0;
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    if (state.visited.indexOf(CALCULATOR_PAGES[i]) >= 0) {
      count += 1;
    }
  }
  if (state.summaryVisited) {
    count += 1;
  }
  return count;
};

export const customizedStepsCount = (state: CalculatorProgressState): number => {
  let count = 0;
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    if (state.changed[CALCULATOR_PAGES[i]]) {
      count += 1;
    }
  }
  if (state.summaryVisited) {
    count += 1;
  }
  return count;
};

export const allCalculatorPagesVisited = (state: CalculatorProgressState): boolean => {
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    if (state.visited.indexOf(CALCULATOR_PAGES[i]) < 0) {
      return false;
    }
  }
  return state.summaryVisited;
};

export const allCalculatorPagesCustomized = (state: CalculatorProgressState): boolean => {
  for (let i = 0; i < CALCULATOR_PAGES.length; i++) {
    if (!state.changed[CALCULATOR_PAGES[i]]) {
      return false;
    }
  }
  return state.summaryVisited;
};

export const calculatorProgressReadyForPoints = (state: CalculatorProgressState): boolean =>
  allCalculatorPagesVisited(state) && allCalculatorPagesCustomized(state);

export const CALCULATOR_ACTION_MESSAGE =
  'To earn points, visit and customize every calculator page (Get Started, Transport, Housing, Food, Shopping) and open your Summary. When you are done, share your footprint to complete this action.';

export const CALCULATOR_DEFAULTS_MESSAGE =
  'Default values are calculated based on your location, household size, and income.';

export const CALCULATOR_REQUIREMENTS_MESSAGE =
  `${CALCULATOR_ACTION_MESSAGE} ${CALCULATOR_DEFAULTS_MESSAGE}`;
